import React, { useState, useEffect, useRef } from 'react';
import { useWebSocketData } from '../context/WebSocketContext';
import { fetchWrapper } from '../utils/fetchWrapper';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  MdSearch,
  MdPeople,
  MdVisibility,
  MdFolder,
  MdEmail
} from 'react-icons/md';
import './Dashboard.css';
import GlobalSearch from './ui/GlobalSearch';
import { formatDate } from '../utils/units';

function Dashboard() {
  const { leads: liveLeads, visits: liveVisits, analytics: liveAnalytics, loading: wsLoading } = useWebSocketData();
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Track previous counts to detect new leads/visits
  const prevLeadsCount = useRef(0);
  const prevVisitsCount = useRef(0);

  // Data state
  const [data, setData] = useState({
    summary: null,
    trends: { daily: { leads: [], visits: [] } },
    leadsByProject: [],
    deviceBreakdown: [],
    geoDistribution: []
  });

  // Map real data to chart formats
  const leadsTrendData = data.trends?.daily?.leads?.map(d => ({ name: d.date, value: d.count })) || [];
  const visitsTrendData = data.trends?.daily?.visits?.map(d => ({ name: d.date, value: d.count })) || [];

  // Map projects to "Menu Items" style
  const topProjects = data.leadsByProject?.slice(0, 3).map(p => ({
    name: p.project,
    price: p.count, // Using count as "price" for display
    revenue: p.count * 10 // Mock revenue calc
  })) || [];

  // Fetch data immediately on mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (liveAnalytics) {
      // Trigger pulsing animation
      setIsUpdating(true);
      setTimeout(() => setIsUpdating(false), 1000);

      setData({
        summary: liveAnalytics.summary,
        trends: liveAnalytics.trends,
        leadsByProject: liveAnalytics.leadsByProject || [],
        deviceBreakdown: liveAnalytics.deviceBreakdown || [],
        geoDistribution: liveAnalytics.geoDistribution || []
      });

      setLastUpdate(new Date());
    }
  }, [liveAnalytics]);

  // INSTANT UPDATE: Listen for new leads and increment count immediately
  useEffect(() => {
    const currentCount = liveLeads.length;
    const diff = currentCount - prevLeadsCount.current;

    // Only increment if we have initial data AND count increased (new lead arrived)
    if (data.summary && diff > 0) {
      const latestLead = liveLeads[0];
      // console.log('🚀 New lead detected!', latestLead?.name || 'New Lead');

      // Trigger pulsing animation
      setIsUpdating(true);
      setTimeout(() => setIsUpdating(false), 1000);

      // Increment total and today counts
      setData(prev => ({
        ...prev,
        summary: {
          ...prev.summary,
          totalLeads: (prev.summary?.totalLeads || 0) + diff,
          todayLeads: (prev.summary?.todayLeads || 0) + diff
        }
      }));

      setLastUpdate(new Date());
    }

    // Update previous count
    prevLeadsCount.current = currentCount;
  }, [liveLeads.length]); // Trigger when new lead arrives

  // INSTANT UPDATE: Listen for new visits and increment count immediately
  useEffect(() => {
    const currentCount = liveVisits.length;
    const diff = currentCount - prevVisitsCount.current;

    // Only increment if we have initial data AND count increased (new visit arrived)
    if (data.summary && diff > 0) {
      const latestVisit = liveVisits[0];
      // console.log('🚀 New visit detected!', latestVisit?.domain || latestVisit?.project || 'Anonymous');

      // Trigger pulsing animation
      setIsUpdating(true);
      setTimeout(() => setIsUpdating(false), 1000);

      // Increment total and today counts
      setData(prev => ({
        ...prev,
        summary: {
          ...prev.summary,
          totalVisits: (prev.summary?.totalVisits || 0) + diff,
          todayVisits: (prev.summary?.todayVisits || 0) + diff
        }
      }));

      setLastUpdate(new Date());
    }

    // Update previous count
    prevVisitsCount.current = currentCount;
  }, [liveVisits.length]); // Trigger when new visit arrives

  const fetchInitialData = async () => {
    setLoading(true);
    try {

      // Fetch all analytics data instantly from one endpoint
      const response = await fetchWrapper.get('/analytics/instant');

      if (response.data.success) {
        const analyticsData = response.data.data;

        setData({
          summary: analyticsData.summary,
          trends: analyticsData.trends,
          leadsByProject: analyticsData.leadsByProject || [],
          deviceBreakdown: analyticsData.deviceBreakdown || [],
          geoDistribution: analyticsData.geoDistribution || []
        });
      }
    } catch (err) {
      console.error('Failed to fetch instant analytics:', err);

      // Fallback to individual API calls if instant endpoint fails
      console.log('🔄 Falling back to individual API calls...');
      try {
        const [summaryRes, leadsByProjectRes, deviceRes, geoRes] =
          await Promise.all([
            fetchWrapper.get('/analytics/summary'),
            fetchWrapper.get('/analytics/leads-by-project'),
            fetchWrapper.get('/analytics/device-breakdown'),
            fetchWrapper.get('/analytics/geo-distribution')
          ]);

        setData(prev => ({
          ...prev,
          summary: summaryRes.data.data,
          leadsByProject: leadsByProjectRes.data.data,
          deviceBreakdown: deviceRes.data.data,
          geoDistribution: geoRes.data.data
        }));
        console.log('✅ Fallback analytics loaded');
      } catch (fallbackErr) {
        console.error('Failed to fetch fallback analytics:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper to format currency/numbers
  const formatNumber = (num) => num ? num.toLocaleString() : '0';

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <h2 className="page-title">Dashboard</h2>
        <div className="header-actions">
          <GlobalSearch />
          <div className="date-display">
            {lastUpdate ? (
              <span title={formatDate(lastUpdate, "dd-mm-yyyy-time")}>
                Last updated: {formatDate(lastUpdate, "time-only")}
                {isUpdating && <span className="ms-2 text-success">●</span>}
              </span>
            ) : (
              <span>Loading...</span>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className={`stat-card ${isUpdating ? 'updating' : ''}`}>
          <div className="stat-header">
            <span className="stat-label">Total Leads</span>
            <div className="stat-icon-bg green">
              <MdPeople size={24} />
            </div>
          </div>
          <div className="stat-body">
            <h3 className="stat-value">{formatNumber(data.summary?.totalLeads || 0)}</h3>
            <span className="stat-change positive">+{data.summary?.todayLeads || 0} today</span>
          </div>
        </div>

        <div className={`stat-card ${isUpdating ? 'updating' : ''}`}>
          <div className="stat-header">
            <span className="stat-label">Total Visits</span>
            <div className="stat-icon-bg blue">
              <MdVisibility size={24} />
            </div>
          </div>
          <div className="stat-body">
            <h3 className="stat-value">{formatNumber(data.summary?.totalVisits || 0)}</h3>
            <span className="stat-change positive">+{data.summary?.todayVisits || 0} today</span>
          </div>
        </div>

        <div className={`stat-card ${isUpdating ? 'updating' : ''}`}>
          <div className="stat-header">
            <span className="stat-label">Active Projects</span>
            <div className="stat-icon-bg purple">
              <MdFolder size={24} />
            </div>
          </div>
          <div className="stat-body">
            <h3 className="stat-value">{formatNumber(data.summary?.totalProjects || 0)}</h3>
            <span className="stat-change positive">Active</span>
          </div>
        </div>

        <div className={`stat-card ${isUpdating ? 'updating' : ''}`}>
          <div className="stat-header">
            <span className="stat-label">Email Success</span>
            <div className="stat-icon-bg orange">
              <MdEmail size={24} />
            </div>
          </div>
          <div className="stat-body">
            <h3 className="stat-value">{data.summary?.emailSuccessRate || 0}%</h3>
            <span className="stat-change positive">{data.summary?.emailStats?.sent || 0} sent</span>
          </div>
        </div>
      </div>

      {/* Main Grid Content */}
      <div className="dashboard-main-grid">
        {/* Left Column */}
        <div className="dashboard-col-left">
          {/* Recent Leads List */}
          <div className="card-base reservations-card">
            <div className="card-header-flex">
              <h4 className="card-title">Recent Leads</h4>
              <button className="btn-link" onClick={() => window.location.href = '/leads'}>View All</button>
            </div>
            <div className="reservations-list">
              {liveLeads.length > 0 ? (
                liveLeads.slice(0, 5).map((lead, index) => (
                  <div key={index} className="reservation-item">
                    <div className="res-avatar">
                      <img src={`https://ui-avatars.com/api/?name=${lead.name}&background=random`} alt={lead.name} />
                    </div>
                    <div className="res-info">
                      <p className="res-name">{lead.name}</p>
                      <p className="res-time">{formatDate(lead.createdAt || lead.timestamp, "time-only")}</p>
                    </div>
                    <div className="res-details">
                      <span className="res-table">{lead.phone}</span>
                    </div>
                    <div className="res-status confirmed">New Lead</div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted py-3">No recent leads</div>
              )}
            </div>
          </div>

          {/* Reservations Per Day Chart -> Visits Trend */}
          <div className="card-base chart-card-small">
            <div className="card-header-flex">
              <h4 className="card-title">Visits Trend</h4>
              <div className="toggle-switch">
                <span className="active">Daily</span>
              </div>
            </div>
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <BarChart data={visitsTrendData} barSize={10}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#999', fontSize: 10 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#999', fontSize: 10 }} />
                  <Tooltip cursor={{ fill: 'transparent' }} />
                  <Bar dataKey="value" fill="#2d6a4f" radius={[10, 10, 10, 10]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="dashboard-col-right">
          {/* Average Check Size Chart -> Leads Trend */}
          <div className="card-base chart-card-large">
            <div className="card-header-flex">
              <h4 className="card-title">Leads Trend</h4>
              <div className="toggle-switch">
                <span className="active">Daily</span>
              </div>
            </div>
            <div style={{ width: '100%', height: 250 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <AreaChart data={leadsTrendData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8884d8" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#999', fontSize: 10 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#999', fontSize: 10 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Popular Menu Items -> Top Projects */}
          <div className="card-base menu-items-card">
            <div className="card-header-flex">
              <h4 className="card-title">Top Projects</h4>
            </div>
            <div className="menu-list-header">
              <span>Project Name</span>
              <div className="text-end">
                <span className="me-4">Leads</span>
                <span>Score</span>
              </div>
            </div>
            <div className="menu-list">
              {topProjects.length > 0 ? (
                topProjects.map((project, i) => (
                  <div key={i} className="menu-item">
                    <div className="menu-info">
                      <div className="menu-img">🚀</div>
                      <span>{project.name}</span>
                    </div>
                    <div className="menu-stats">
                      <span className="menu-price">{project.price}</span>
                      <span className="menu-revenue">{project.revenue}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted">No project data available</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
